import React, { useCallback, useMemo, useState } from 'react';
import { List } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensions } from 'flow/selectors/ExtensionSelectors';

import useAppContext from 'flow/hooks/useAppContext';
import { sortByName } from 'flow/utils';
import { COLISTENING_WIDGET_USERS } from 'flow/utils/browser-utils';
import {
    filterExtensionsBySearchableInfo,
    getFixedNumberFromContact,
    getMobileNumberFromContact,
} from 'flow/utils/extension-utils';
import { twMerge } from 'tailwind-merge';
import BaseModal from 'flow/components/ui-components/BaseModal';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import IconButton from 'flow/components/ui-components/IconButton';
import Icon from 'flow/components/ui-components/Icon';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { TypedMap } from 'flow/types/TypedMap';

import FilterSearchBar from 'flow/components/FilterSearchBar';
import VariableSizeWindowingList from 'flow/components/windowing/VariableSizeWindowingList_TW';
import ExtensionSelectable from 'flow/components/tickets/ticket-details/AddMembers/ExtensionSelectable';

interface Props {
    onClose?: () => void;
}

const AddExtensionsToAutoCoListenToModal = ({ onClose }: Props) => {
    const { i18next } = useAppContext();
    const extensions = useAppSelector(selectExtensions);

    const extensionsAlreadyAdded = useMemo(() => {
        try {
            const storedUsers = localStorage.getItem(COLISTENING_WIDGET_USERS);
            if (!storedUsers) return List();
            return List(JSON.parse(storedUsers));
        } catch {
            return List();
        }
    }, []);

    const listItems: React.JSX.Element[] = [];
    const listItemHeights: number[] = [];
    const getItemSize = (index: number) => listItemHeights[index];

    const addListItem = (item: React.JSX.Element, height: number) => {
        listItems.push(item);
        listItemHeights.push(height);
    };

    const createExtensionSelectableItem = (extension: TypedMap<Extension>) => {
        const item = (
            <ExtensionSelectable
                key={`selectable-${extension.get('key')}`}
                extensionKey={extension.get('key')}
                onClickAdd={onClickSelect}
            />
        );
        addListItem(item, 52);
    };

    const createListItems = () => {
        filterExtensionsBySearchableInfo(
            extensionsToDisplayInSelectableList.sort(sortByName),
            filterText,
        ).map(createExtensionSelectableItem);
    };

    const [filterText, setFilterText] = useState('');
    const [extensionKeysSelected, setExtensionKeysSelected] =
        useState<List<ExtensionKey>>(List());

    const extensionsToDisplayInSelectableList = useMemo(
        () =>
            extensions.filter(
                (extension: TypedMap<Extension>) =>
                    !extensionsAlreadyAdded.contains(extension.get('key')) &&
                    !extensionKeysSelected.contains(extension.get('key')),
            ),
        [extensions, extensionKeysSelected, extensionsAlreadyAdded],
    );

    const onClickSubmit = useCallback(() => {
        const usersToSave = extensionsAlreadyAdded
            .concat(extensionKeysSelected)
            .toArray();
        localStorage.setItem(
            COLISTENING_WIDGET_USERS,
            JSON.stringify(usersToSave),
        );
        onClose?.();
    }, [onClose, extensionsAlreadyAdded, extensionKeysSelected]);

    const onClickSelect = useCallback(
        (extensionKey: ExtensionKey) => {
            setExtensionKeysSelected(extensionKeysSelected.push(extensionKey));
            setFilterText('');
        },
        [extensionKeysSelected],
    );

    const onClickDeselect = useCallback(
        (extensionKey: ExtensionKey) => {
            const deselectedIndex = extensionKeysSelected.findIndex(
                (key) => key === extensionKey,
            );
            setExtensionKeysSelected(
                extensionKeysSelected.remove(deselectedIndex),
            );
        },
        [extensionKeysSelected],
    );

    createListItems();

    return (
        <BaseModal
            open={true}
            header={i18next.t('Add users to automatically listen to')}
            onClose={onClose}
            confirmButtonText={i18next.t('chatsettings.add')}
            className="w-[600px]"
            onSubmit={onClickSubmit}
            disablePadding={true}
        >
            <div className="flex flex-col gap-3 ps-6 pe-4">
                <FilterSearchBar
                    searchText={filterText}
                    placeholder={i18next.t('input.filter')}
                    handleFilter={(_event, value) => {
                        setFilterText(value);
                    }}
                />

                {!extensionKeysSelected.isEmpty() ? (
                    <div className="flex flex-wrap gap-3">
                        {extensionKeysSelected
                            .map((extensionKey) => {
                                const extension = extensions.get(extensionKey);
                                return (
                                    <div
                                        key={extension.get('key')}
                                        className="flex items-center rounded-sm border border-gray-300 p-1"
                                    >
                                        <ContactAvatar
                                            contact={extension.get('contact')}
                                            size="small"
                                            fallBackBackgroundColorClass={twMerge(
                                                'group-hover:bg-gray-300 bg-gray-200',
                                            )}
                                        />
                                        <div className="ms-2 me-3 text-sm">
                                            {extension.getIn(
                                                ['contact', 'firstName'],
                                                '',
                                            ) ||
                                                getFixedNumberFromContact(
                                                    extension.get('contact'),
                                                ) ||
                                                getMobileNumberFromContact(
                                                    extension.get('contact'),
                                                ) ||
                                                i18next.t('Contact.Unknown')}
                                        </div>
                                        <div>
                                            <IconButton
                                                aria-label={i18next.t('remove')}
                                                className="size-6"
                                                onClick={() =>
                                                    onClickDeselect(
                                                        extensionKey,
                                                    )
                                                }
                                            >
                                                <Icon iconKey="cancel" />
                                            </IconButton>
                                        </div>
                                    </div>
                                );
                            })
                            .toArray()}
                    </div>
                ) : null}
                <div className="h-96 overflow-y-scroll">
                    <VariableSizeWindowingList
                        items={listItems}
                        getItemSize={getItemSize}
                    />
                </div>
            </div>
        </BaseModal>
    );
};

export default AddExtensionsToAutoCoListenToModal;
